import { Checkbox, DatePicker, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { cn } from '@/libs/utils';
import { DATE_FORMATS } from '@/utils/formUtils';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';
import { EmploymentTypeControl } from '../EmploymentTypeControl';

/** Contract, sector and the effective date of an employment change. */
export default function EmployeeContractFields() {
  const {
    COMPANY_DATA,
    companyDataIsLoading,
    effectiveDateItem,
    employeeSectorKeys,
    employeeSectorState,
    employmentTypeKeys,
    employmentTypeState,
    endDateKeys,
    endDateState,
    hasCompanySector,
    inSection,
    isHrOfficerKeys,
    isHrOfficerState,
    isProfile,
    orgUnitKeys,
    orgUnitState,
    sectionClass,
    showEndDate,
    startDate,
    startDateKeys,
    startDateState,
    t,
  } = useEmployeeGeneralDataForm();

  return (
    <>
      {!isProfile && <hr className="mb-5" />}

      <div
        className={cn(
          !isProfile && 'mb-5 rounded border bg-zinc-50 p-4',
          isProfile && !inSection('ugovor') && !inSection('org') && 'hidden'
        )}
      >
        <div className="grid gap-4">
          {/* On the profile: EmployeeContractProfilePanel. */}
          {!isProfile && (
            <div className={cn('grid gap-4', sectionClass('ugovor'))}>
              {employmentTypeState?.visible && (
                <Form.Item
                  name={employmentTypeKeys}
                  label={t('employmentType', { ns: 'forms' })}
                  className="!mb-1"
                >
                  <EmploymentTypeControl
                    disabled={employmentTypeState?.disabled}
                    t={t}
                  />
                </Form.Item>
              )}

              <div className="flex gap-5">
                {startDateState?.visible && (
                  <Form.Item
                    name={startDateKeys}
                    label={t('startDate', { ns: 'forms' })}
                    className="!mb-1 w-full"
                    rules={[
                      {
                        required: true,
                        message: t('formValidation.selectDateRequired', {
                          ns: 'forms',
                        }),
                      },
                    ]}
                  >
                    <DatePicker
                      className="w-full"
                      format={DATE_FORMATS.DISPLAY}
                      placeholder={t('startDateOfWork', { ns: 'forms' })}
                      disabled={startDateState?.disabled}
                    />
                  </Form.Item>
                )}

                {showEndDate && endDateState?.visible && (
                  <Form.Item
                    name={endDateKeys}
                    label={t('endDate', { ns: 'forms' })}
                    className="!mb-1 w-full"
                    rules={[
                      {
                        required: true,
                        message: t('formValidation.selectDateRequired', {
                          ns: 'forms',
                        }),
                      },
                    ]}
                  >
                    <DatePicker
                      minDate={dayjs(startDate, DATE_FORMATS.STANDARD)}
                      className="w-full"
                      format={DATE_FORMATS.DISPLAY}
                      placeholder={t('endDateOfWork', { ns: 'forms' })}
                      disabled={endDateState?.disabled}
                    />
                  </Form.Item>
                )}

                {!isProfile && effectiveDateItem}
              </div>
            </div>
          )}

          {/* On the profile: EmployeeOrgProfilePanel. */}
          {!isProfile && hasCompanySector && (
            <div className={sectionClass('org')}>
              {!isProfile && <hr className="mb-5" />}

              {employeeSectorState?.visible && (
                <Form.Item
                  name={employeeSectorKeys}
                  label={t('sector', { ns: 'forms' })}
                  rules={[
                    {
                      required: true,
                      message: t('formValidation.sectorRequired', {
                        ns: 'forms',
                      }),
                    },
                  ]}
                >
                  <Select
                    allowClear
                    mode="single"
                    placeholder={t('sector', { ns: 'forms' })}
                    className="w-full"
                    loading={companyDataIsLoading}
                    options={COMPANY_DATA.sectors}
                    disabled={employeeSectorState?.disabled}
                  />
                </Form.Item>
              )}

              {isHrOfficerState?.visible && (
                <Form.Item
                  name={isHrOfficerKeys}
                  label={null}
                  valuePropName="checked"
                  className="!mb-0"
                >
                  <Checkbox disabled={isHrOfficerState?.disabled}>
                    <span>{t('hrOfficer', { ns: 'forms' })}</span>{' '}
                    <span className="lowercase">
                      ({t('role', { ns: 'forms' })})
                    </span>
                  </Checkbox>
                </Form.Item>
              )}
            </div>
          )}

          {/* Shown for every company, with or without sectors. */}
          {!isProfile && orgUnitState?.visible && (
            <div className={sectionClass('org')}>
              {!isProfile && <hr className="mb-5" />}
              <Form.Item
                name={orgUnitKeys}
                label={t('orgUnit', { ns: 'forms' })}
                extra={t('orgUnitHint', { ns: 'forms' })}
                className="!mb-0"
              >
                <Input
                  allowClear
                  disabled={orgUnitState?.disabled}
                  placeholder={t('orgUnit', { ns: 'forms' })}
                />
              </Form.Item>
            </div>
          )}
        </div>
      </div>

      {isProfile && !inSection('ugovor') && effectiveDateItem}
    </>
  );
}
